import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { staffApi, plaidApi, hireInvitesApi, documentsApi, trainingsApi, availabilityApi, ComplianceDocument, EmployeeTraining } from '../services/api';
import {
  Plus,
  Search,
  MoreVertical,
  Clock,
  Edit2,
  Trash2,
  User,
  Building2,
  CheckCircle,
  AlertTriangle,
  Shield,
  Loader2,
  Mail,
  X,
  FileText,
  CreditCard,
  Calendar,
  Phone,
  MapPin,
  UserCheck,
  ClipboardCheck,
  Send,
  Settings,
  Upload,
  Eye,
  Download,
  File,
  Image,
  GraduationCap,
  Award,
  PlayCircle,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';
import { BankInviteModal } from '../components/PlaidLink';

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  primaryRole: string;
  hourlyRateCents?: number;
  isActive: boolean;
  isClockedIn?: boolean;
  bankAccountId?: string;
  status?: 'active' | 'inactive' | 'terminated';
  terminationDate?: string;
  // Onboarding/compliance fields
  onboardingStatus?: 'pending' | 'invited' | 'in_progress' | 'awaiting_review' | 'ready' | null;
  complianceStatus?: 'pending' | 'compliant' | 'non_compliant' | null;
  canBeScheduled?: boolean;
  schedulingRestrictions?: Record<string, boolean>;
  // Additional detail fields
  hireDate?: string;
  dateOfBirth?: string;
  workAuthStatus?: string;
  workAuthExpiresAt?: string;
  i9CompletedAt?: string;
  w4CompletedAt?: string;
  onboardingCompletedAt?: string;
  addressStreet1?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountLast4: string;
  accountType: string;
  status: string;
}

export default function StaffPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [onboardingFilter, setOnboardingFilter] = useState<string>('all');
  const [showTerminated, setShowTerminated] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    primaryRole: 'server',
    pin: '',
    hourlyRateCents: 1500,
  });
  const [formError, setFormError] = useState('');
  const [bankInviteModal, setBankInviteModal] = useState<{
    isOpen: boolean;
    employeeId: string;
    employeeName: string;
    employeeEmail?: string;
  } | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<StaffMember | null>(null);
  const [employeeDocuments, setEmployeeDocuments] = useState<ComplianceDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [editContactData, setEditContactData] = useState({
    email: '',
    phone: '',
    addressStreet1: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
  });
  const [savingContact, setSavingContact] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [employeeTrainings, setEmployeeTrainings] = useState<EmployeeTraining[]>([]);
  const [loadingTrainings, setLoadingTrainings] = useState(false);
  const [employeeAvailability, setEmployeeAvailability] = useState<any[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Document verification modal state
  const [verifyingDocument, setVerifyingDocument] = useState<ComplianceDocument | null>(null);
  const [verificationData, setVerificationData] = useState({
    verificationNotes: '',
    expirationDate: '',
    documentNumber: '',
    issuingAuthority: '',
  });
  const [submittingVerification, setSubmittingVerification] = useState(false);

  // Fetch documents and trainings when an employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeDocuments(selectedEmployee.id);
      fetchEmployeeTrainings(selectedEmployee.id);
      fetchEmployeeAvailability(selectedEmployee.id);
    } else {
      setEmployeeDocuments([]);
      setEmployeeTrainings([]);
      setEmployeeAvailability([]);
    }
  }, [selectedEmployee?.id]);

  const fetchEmployeeAvailability = async (employeeId: string) => {
    setLoadingAvailability(true);
    try {
      const response = await availabilityApi.getByEmployee(employeeId);
      setEmployeeAvailability(response.data?.data?.windows || []);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      setEmployeeAvailability([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const fetchEmployeeDocuments = async (employeeId: string) => {
    setLoadingDocuments(true);
    try {
      const response = await documentsApi.getByEmployee(employeeId);
      setEmployeeDocuments(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setEmployeeDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchEmployeeTrainings = async (employeeId: string) => {
    setLoadingTrainings(true);
    try {
      const response = await trainingsApi.getEmployeeTrainings(employeeId);
      setEmployeeTrainings(response.data?.data?.trainings || []);
    } catch (error) {
      console.error('Failed to fetch trainings:', error);
      setEmployeeTrainings([]);
    } finally {
      setLoadingTrainings(false);
    }
  };

  const handleStartTraining = async (trainingId: string) => {
    try {
      await trainingsApi.startTraining(trainingId);
      if (selectedEmployee) {
        await fetchEmployeeTrainings(selectedEmployee.id);
      }
    } catch (error: any) {
      console.error('Start training failed:', error);
      alert(error.response?.data?.error || 'Failed to start training');
    }
  };

  const handleCompleteTraining = async (trainingId: string) => {
    const method = prompt(
      'Select verification method:\n\n1. Manager Sign-off\n2. Quiz Passed\n3. Certificate Uploaded\n4. Self Reported\n\nEnter number:',
      '1'
    );
    const methodMap: Record<string, 'manager_sign_off' | 'quiz_passed' | 'certificate_uploaded' | 'self_reported'> = {
      '1': 'manager_sign_off',
      '2': 'quiz_passed',
      '3': 'certificate_uploaded',
      '4': 'self_reported',
    };
    const verificationMethod = methodMap[method || '1'] || 'manager_sign_off';

    try {
      await trainingsApi.completeTraining(trainingId, { verificationMethod });
      if (selectedEmployee) {
        await fetchEmployeeTrainings(selectedEmployee.id);
      }
      alert('Training marked as completed');
    } catch (error: any) {
      console.error('Complete training failed:', error);
      alert(error.response?.data?.error || 'Failed to complete training');
    }
  };

  const handleDocumentUpload = async (file: File, documentType: string) => {
    if (!selectedEmployee) return;

    setUploadingDocument(true);
    try {
      await documentsApi.upload(selectedEmployee.id, file, documentType);
      // Refresh documents list
      await fetchEmployeeDocuments(selectedEmployee.id);
    } catch (error: any) {
      console.error('Upload failed:', error);
      alert(error.response?.data?.error?.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const openVerifyModal = (doc: ComplianceDocument) => {
    setVerifyingDocument(doc);
    setVerificationData({
      verificationNotes: '',
      expirationDate: doc.expirationDate || '',
      documentNumber: doc.documentNumber || '',
      issuingAuthority: doc.issuingAuthority || '',
    });
  };

  const handleVerifyDocument = async () => {
    if (!verifyingDocument) return;

    setSubmittingVerification(true);
    try {
      await documentsApi.verify(verifyingDocument.id, {
        verificationNotes: verificationData.verificationNotes || undefined,
        expirationDate: verificationData.expirationDate || undefined,
        documentNumber: verificationData.documentNumber || undefined,
        issuingAuthority: verificationData.issuingAuthority || undefined,
      });
      // Refresh documents
      if (selectedEmployee) {
        await fetchEmployeeDocuments(selectedEmployee.id);
      }
      setVerifyingDocument(null);
    } catch (error: any) {
      console.error('Verify failed:', error);
      alert(error.response?.data?.error?.message || 'Failed to verify document');
    } finally {
      setSubmittingVerification(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentsApi.delete(documentId);
      // Refresh documents
      if (selectedEmployee) {
        await fetchEmployeeDocuments(selectedEmployee.id);
      }
    } catch (error: any) {
      console.error('Delete failed:', error);
      alert(error.response?.data?.error?.message || 'Failed to delete document');
    }
  };

  const startEditingContact = () => {
    if (!selectedEmployee) return;
    setEditContactData({
      email: selectedEmployee.email || '',
      phone: selectedEmployee.phone || '',
      addressStreet1: selectedEmployee.addressStreet1 || '',
      addressCity: selectedEmployee.addressCity || '',
      addressState: selectedEmployee.addressState || '',
      addressPostalCode: selectedEmployee.addressPostalCode || '',
      emergencyContactName: selectedEmployee.emergencyContactName || '',
      emergencyContactPhone: selectedEmployee.emergencyContactPhone || '',
    });
    setEditingContact(true);
  };

  const saveContactInfo = async () => {
    if (!selectedEmployee) return;
    setSavingContact(true);
    try {
      await staffApi.update(selectedEmployee.id, editContactData);
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setSelectedEmployee({ ...selectedEmployee, ...editContactData });
      setEditingContact(false);
      alert('Contact information updated successfully');
    } catch (error: any) {
      console.error('Update failed:', error);
      alert(error.response?.data?.error?.message || 'Failed to update contact info');
    } finally {
      setSavingContact(false);
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      id_document: 'Photo ID',
      work_authorization: 'Work Authorization',
      i9_list_a: 'I-9 List A Document',
      i9_list_b: 'I-9 List B Document',
      i9_list_c: 'I-9 List C Document',
      w4_form: 'W-4 Tax Form',
      state_tax_form: 'State Tax Form',
      direct_deposit: 'Direct Deposit Verification',
      food_handler_cert: 'Food Handler Certificate',
      alcohol_server_cert: 'Alcohol Server Certificate',
      other: 'Other Document',
    };
    return labels[type] || type;
  };

  const { data: staff, isLoading } = useQuery({
    queryKey: ['staff', showTerminated],
    queryFn: async () => {
      // Pass isActive=false to include terminated employees when showTerminated is true
      const response = showTerminated
        ? await staffApi.getAll({ includeTerminated: true })
        : await staffApi.getAll();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => hireInvitesApi.create({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      primaryRole: data.primaryRole,
      wageType: 'hourly',
      proposedHourlyRateCents: data.hourlyRateCents,
      pin: data.pin, // Send the PIN set by manager
      foodHandlingInvolved: ['cook', 'server', 'bartender'].includes(data.primaryRole),
      alcoholServiceInvolved: ['bartender', 'server'].includes(data.primaryRole),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      setShowAddModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        primaryRole: 'server',
        pin: '',
        hourlyRateCents: 1500,
      });
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to add staff member');
    },
  });

  const roles = ['all', 'server', 'cook', 'host', 'manager', 'bartender'];

  // Use real data only - no mock fallbacks
  const staffData = Array.isArray(staff) ? staff : [];

  const filteredStaff = staffData.filter((member) => {
    const matchesSearch =
      `${member.firstName} ${member.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || member.primaryRole.toLowerCase() === selectedRole.toLowerCase();
    const st = member.onboardingStatus || 'pending';
    const matchesOnboarding = onboardingFilter === 'all' ||
      (onboardingFilter === 'needs_you' && (st === 'pending' || st === 'awaiting_review')) ||
      (onboardingFilter === 'in_progress' && (st === 'invited' || st === 'in_progress')) ||
      (onboardingFilter === 'ready' && st === 'ready');
    return matchesSearch && matchesRole && matchesOnboarding;
  });

  // Onboarding status counts
  const onboardingCounts = {
    pending: staffData.filter((s) => s.onboardingStatus === 'pending' || !s.onboardingStatus).length,
    invited: staffData.filter((s) => s.onboardingStatus === 'invited').length,
    in_progress: staffData.filter((s) => s.onboardingStatus === 'in_progress').length,
    awaiting_review: staffData.filter((s) => s.onboardingStatus === 'awaiting_review').length,
    ready: staffData.filter((s) => s.onboardingStatus === 'ready').length,
  };

  // Helper function for onboarding status badge
  const getOnboardingBadge = (status?: string | null) => {
    switch (status) {
      case 'invited':
        return (
          <span className="badge bg-blue-100 text-blue-700 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            Invited
          </span>
        );
      case 'in_progress':
        return (
          <span className="badge bg-amber-100 text-amber-700 flex items-center gap-1">
            <Loader2 className="w-3 h-3" />
            In Progress
          </span>
        );
      case 'awaiting_review':
        return (
          <span className="badge bg-purple-100 text-purple-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Awaiting Review
          </span>
        );
      case 'ready':
        return (
          <span className="badge badge-success flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Ready
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="badge bg-slate-100 text-slate-600 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  const formatCurrency = (cents: number) =>
    `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Staff</h1>
            <p className="text-slate-500">Manage your team members</p>
          </div>
          <HelpButton featureId="staff" />
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/staff/onboarding-settings"
            className="btn btn-secondary flex items-center gap-2"
            title="Configure onboarding email content"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Onboarding Settings</span>
          </Link>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Staff Member
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search staff..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {roles.map((role) => (
              <button
                key={role}
                onClick={() => setSelectedRole(role)}
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors',
                  selectedRole === role
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                )}
              >
                {role === 'all' ? 'All Roles' : role.charAt(0).toUpperCase() + role.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Show Terminated Toggle & Onboarding Status Filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showTerminated}
              onChange={(e) => setShowTerminated(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-slate-600">Show Terminated</span>
          </label>

          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Onboarding:</span>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {[
              { id: 'all', label: 'All', count: staffData.length },
              { id: 'needs_you', label: 'Needs you', count: onboardingCounts.pending + onboardingCounts.awaiting_review },
              { id: 'in_progress', label: 'In progress', count: onboardingCounts.invited + onboardingCounts.in_progress },
              { id: 'ready', label: 'Ready', count: onboardingCounts.ready },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setOnboardingFilter(filter.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5',
                  onboardingFilter === filter.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                )}
              >
                {filter.label}
                {filter.count > 0 && (
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded-full text-xs',
                    onboardingFilter === filter.id
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 text-slate-600'
                  )}>
                    {filter.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-sm text-slate-500">Total Staff</p>
          <p className="text-2xl font-bold">{staffData.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-slate-500">Currently Clocked In</p>
          <p className="text-2xl font-bold text-success-600">
            {staffData.filter((s) => s.isClockedIn).length}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-slate-500">Active</p>
          <p className="text-2xl font-bold">
            {staffData.filter((s) => s.isActive).length}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-slate-500">Avg Hourly Rate</p>
          <p className="text-2xl font-bold">
            {formatCurrency(
              Math.round(
                staffData.reduce((sum, s) => sum + (s.hourlyRateCents || 0), 0) /
                  staffData.length
              )
            )}
          </p>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {filteredStaff.map((member) => (
          <div
            key={member.id}
            className={clsx(
              'card p-4',
              member.status === 'terminated' && 'bg-red-50/50 opacity-75'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <button
                onClick={() => setSelectedEmployee(member)}
                className="flex items-center gap-3 text-left"
              >
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                  member.status === 'terminated' ? 'bg-red-100' : 'bg-slate-100'
                )}>
                  <User className={clsx(
                    'w-5 h-5',
                    member.status === 'terminated' ? 'text-red-400' : 'text-slate-500'
                  )} />
                </div>
                <div>
                  <p className={clsx(
                    'font-medium',
                    member.status === 'terminated'
                      ? 'text-red-600 line-through'
                      : 'text-slate-900'
                  )}>
                    {member.firstName} {member.lastName}
                  </p>
                  <p className="text-sm text-slate-500 capitalize">{member.primaryRole}</p>
                </div>
              </button>
              <div className="flex flex-col items-end gap-1">
                {member.status === 'terminated' ? (
                  <span className="badge bg-red-100 text-red-700 text-xs">Terminated</span>
                ) : member.isClockedIn ? (
                  <span className="badge badge-success text-xs">Clocked In</span>
                ) : (
                  <span className="badge bg-slate-100 text-slate-600 text-xs">Off</span>
                )}
                {getOnboardingBadge(member.onboardingStatus)}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-slate-500">Rate:</span>{' '}
                <span className="font-medium">
                  {member.hourlyRateCents ? formatCurrency(member.hourlyRateCents) + '/hr' : '-'}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Bank:</span>{' '}
                {member.bankAccountId ? (
                  <span className="text-success-600">Connected</span>
                ) : (
                  <button
                    onClick={() => setBankInviteModal({
                      isOpen: true,
                      employeeId: member.id,
                      employeeName: `${member.firstName} ${member.lastName}`,
                      employeeEmail: member.email,
                    })}
                    className="text-primary-600"
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
            {(member.email || member.phone) && (
              <div className="mt-2 text-xs text-slate-500">
                {member.email && <p>{member.email}</p>}
                {member.phone && <p>{member.phone}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Staff Table - Desktop only */}
      <div className="card overflow-hidden hidden md:block">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="table-header">Name</th>
              <th className="table-header">Role</th>
              <th className="table-header">Contact</th>
              <th className="table-header">Hourly Rate</th>
              <th className="table-header">Status</th>
              <th className="table-header">Onboarding</th>
              <th className="table-header">Bank</th>
              <th className="table-header"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredStaff.map((member) => (
              <tr key={member.id} className={clsx(
                'hover:bg-slate-50',
                member.status === 'terminated' && 'bg-red-50/50 opacity-75'
              )}>
                <td className="table-cell">
                  <button
                    onClick={() => setSelectedEmployee(member)}
                    className="flex items-center gap-3 hover:bg-slate-100 -m-2 p-2 rounded-lg transition-colors text-left w-full"
                  >
                    <div className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      member.status === 'terminated' ? 'bg-red-100' : 'bg-slate-100'
                    )}>
                      <User className={clsx(
                        'w-5 h-5',
                        member.status === 'terminated' ? 'text-red-400' : 'text-slate-500'
                      )} />
                    </div>
                    <div>
                      <p className={clsx(
                        'font-medium',
                        member.status === 'terminated'
                          ? 'text-red-600 line-through'
                          : 'text-primary-600 hover:text-primary-700'
                      )}>
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="text-xs text-slate-500">
                        {member.status === 'terminated' ? 'Terminated' : 'Click for details'}
                      </p>
                    </div>
                  </button>
                </td>
                <td className="table-cell">
                  <span className="badge badge-info capitalize">{member.primaryRole}</span>
                </td>
                <td className="table-cell">
                  <div className="text-sm">
                    {member.email && <p>{member.email}</p>}
                    {member.phone && (
                      <p className="text-slate-500">{member.phone}</p>
                    )}
                  </div>
                </td>
                <td className="table-cell">
                  {member.hourlyRateCents
                    ? formatCurrency(member.hourlyRateCents) + '/hr'
                    : '-'}
                </td>
                <td className="table-cell">
                  <div className="flex items-center gap-2">
                    {member.status === 'terminated' ? (
                      <span className="badge bg-red-100 text-red-700 flex items-center gap-1">
                        <X className="w-3 h-3" />
                        Terminated
                      </span>
                    ) : member.isClockedIn ? (
                      <span className="badge badge-success flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Clocked In
                      </span>
                    ) : (
                      <span className="badge bg-slate-100 text-slate-600">
                        Off
                      </span>
                    )}
                  </div>
                </td>
                <td className="table-cell">
                  <div className="flex flex-col gap-1">
                    {getOnboardingBadge(member.onboardingStatus)}
                    {member.canBeScheduled === false && (
                      <span className="text-xs text-red-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Cannot schedule
                      </span>
                    )}
                  </div>
                </td>
                <td className="table-cell">
                  {member.bankAccountId ? (
                    <span className="badge badge-success flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Connected
                    </span>
                  ) : (
                    <button
                      onClick={() => setBankInviteModal({
                        isOpen: true,
                        employeeId: member.id,
                        employeeName: `${member.firstName} ${member.lastName}`,
                        employeeEmail: member.email,
                      })}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Building2 className="w-3 h-3" />
                      Connect
                    </button>
                  )}
                </td>
                <td className="table-cell">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        const action = prompt(
                          `Edit ${member.firstName} ${member.lastName}\n\nEnter:\n1 - Edit hourly rate\n2 - Reset PIN\n3 - Connect bank account\n4 - Mark as Ready (manager override)\n\nChoice:`,
                          '1'
                        );
                        if (action === '1') {
                          const newRate = prompt(
                            `Edit hourly rate (current: ${member.hourlyRateCents ? (member.hourlyRateCents / 100).toFixed(2) : '0.00'})`,
                            member.hourlyRateCents ? (member.hourlyRateCents / 100).toFixed(2) : '15.00'
                          );
                          if (newRate) {
                            staffApi.update(member.id, { hourlyRateCents: Math.round(parseFloat(newRate) * 100) })
                              .then(() => queryClient.invalidateQueries({ queryKey: ['staff'] }))
                              .catch(() => alert('Failed to update'));
                          }
                        } else if (action === '2') {
                          const newPin = prompt(
                            'Enter new PIN (4-6 digits):',
                            ''
                          );
                          if (newPin && newPin.length >= 4 && newPin.length <= 6) {
                            staffApi.update(member.id, { pin: newPin })
                              .then(() => {
                                queryClient.invalidateQueries({ queryKey: ['staff'] });
                                alert(`PIN updated for ${member.firstName}. They can now use this PIN to login to POS.`);
                              })
                              .catch(() => alert('Failed to update PIN'));
                          } else if (newPin) {
                            alert('PIN must be 4-6 digits');
                          }
                        } else if (action === '3') {
                          setBankInviteModal({
                            isOpen: true,
                            employeeId: member.id,
                            employeeName: `${member.firstName} ${member.lastName}`,
                            employeeEmail: member.email,
                          });
                        } else if (action === '4') {
                          if (confirm(`Mark ${member.firstName} as ready to schedule? This overrides any pending compliance requirements.`)) {
                            staffApi.update(member.id, { onboardingStatus: 'ready', canBeScheduled: true } as any)
                              .then(() => {
                                queryClient.invalidateQueries({ queryKey: ['staff'] });
                                alert(`${member.firstName} is now marked as ready to schedule.`);
                              })
                              .catch(() => alert('Failed to update status'));
                          }
                        }
                      }}
                      className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-primary-600"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (!member.email) {
                          alert('No email address on file. Please add an email to send onboarding.');
                          return;
                        }
                        if (confirm(`Send onboarding email to ${member.firstName} ${member.lastName} at ${member.email}?`)) {
                          staffApi.resendOnboarding(member.id)
                            .then(() => {
                              alert(`Onboarding email sent to ${member.email}`);
                            })
                            .catch((err) => {
                              console.error('Resend error:', err);
                              alert(err.response?.data?.error?.message || 'Failed to send email. Check that RESEND_API_KEY is configured.');
                            });
                        }
                      }}
                      className={clsx(
                        "p-2 hover:bg-slate-100 rounded-lg",
                        member.email ? "text-slate-500 hover:text-blue-600" : "text-slate-300 cursor-not-allowed"
                      )}
                      title={member.email ? "Send onboarding email" : "No email address"}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    {member.status !== 'terminated' && (
                      <button
                        onClick={() => {
                          if (confirm(`Are you sure you want to remove ${member.firstName} ${member.lastName}? They will be marked as terminated.`)) {
                            staffApi.delete(member.id)
                              .then(() => {
                                queryClient.invalidateQueries({ queryKey: ['staff'] });
                                alert(`${member.firstName} ${member.lastName} has been removed.`);
                              })
                              .catch((err) => {
                                console.error('Delete error:', err);
                                alert('Failed to remove employee. Please try again.');
                              });
                          }
                        }}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-danger-600"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredStaff.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No staff members found</p>
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Add Staff Member</h2>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate(formData);
            }}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="Smith"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="input w-full"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    className="input w-full"
                    placeholder="555-0100"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select
                    className="input w-full"
                    value={formData.primaryRole}
                    onChange={(e) => setFormData({ ...formData, primaryRole: e.target.value })}
                  >
                    <option value="server">Server</option>
                    <option value="cook">Cook</option>
                    <option value="host">Host</option>
                    <option value="bartender">Bartender</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Hourly Rate ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input w-full"
                    placeholder="15.00"
                    value={(formData.hourlyRateCents / 100).toFixed(2)}
                    onChange={(e) => setFormData({ ...formData, hourlyRateCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">PIN (4-6 digits)</label>
                  <input
                    type="password"
                    className="input w-full"
                    placeholder="****"
                    maxLength={6}
                    value={formData.pin}
                    onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Adding...' : 'Add Staff Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="w-7 h-7 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </h2>
                  <p className="text-slate-500 capitalize">{selectedEmployee.primaryRole}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Onboarding Status Overview */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-primary-600" />
                    Onboarding Status
                  </h3>
                  {getOnboardingBadge(selectedEmployee.onboardingStatus)}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Can Be Scheduled</p>
                    <p className={clsx(
                      'font-semibold',
                      selectedEmployee.canBeScheduled ? 'text-green-600' : 'text-red-600'
                    )}>
                      {selectedEmployee.canBeScheduled ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Compliance Status</p>
                    <p className={clsx(
                      'font-semibold capitalize',
                      selectedEmployee.complianceStatus === 'compliant' ? 'text-green-600' :
                      selectedEmployee.complianceStatus === 'non_compliant' ? 'text-red-600' : 'text-amber-600'
                    )}>
                      {selectedEmployee.complianceStatus || 'Pending'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Onboarding Checklist */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Onboarding Checklist
                  <span className="text-xs text-slate-400 font-normal ml-2">(Click to override)</span>
                </h3>
                <div className="space-y-2">
                  {[
                    { key: 'dateOfBirth', label: 'Personal Information', completed: !!selectedEmployee.dateOfBirth, icon: User, field: 'dateOfBirth' },
                    { key: 'i9', label: 'I-9 Form (Employment Eligibility)', completed: !!selectedEmployee.i9CompletedAt, icon: FileText, field: 'i9CompletedAt' },
                    { key: 'w4', label: 'W-4 Tax Form', completed: !!selectedEmployee.w4CompletedAt, icon: FileText, field: 'w4CompletedAt' },
                    { key: 'bank', label: 'Direct Deposit Setup', completed: !!selectedEmployee.bankAccountId, icon: CreditCard, field: 'bankAccountId' },
                    { key: 'workAuth', label: 'Work Authorization', completed: selectedEmployee.workAuthStatus === 'verified', icon: UserCheck, field: 'workAuthStatus' },
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() => {
                        if (item.completed) {
                          // Already complete - ask if they want to mark as incomplete
                          if (confirm(`Mark "${item.label}" as incomplete? This will require the employee to complete it again.`)) {
                            const updateData: any = {};
                            if (item.field === 'workAuthStatus') {
                              updateData.workAuthStatus = 'pending_verification';
                            } else if (item.field === 'bankAccountId') {
                              alert('Bank account status cannot be changed from here. Use the Connect Bank option instead.');
                              return;
                            } else {
                              updateData[item.field] = null;
                            }
                            staffApi.update(selectedEmployee.id, updateData)
                              .then(() => {
                                queryClient.invalidateQueries({ queryKey: ['staff'] });
                                const updatedEmployee = { ...selectedEmployee };
                                if (item.field === 'workAuthStatus') {
                                  updatedEmployee.workAuthStatus = 'pending_verification';
                                } else {
                                  (updatedEmployee as any)[item.field] = null;
                                }
                                setSelectedEmployee(updatedEmployee);
                              })
                              .catch(() => alert('Failed to update'));
                          }
                        } else {
                          // Not complete - allow manager override
                          if (confirm(`Mark "${item.label}" as complete? (Manager Override)\n\nThis should only be done if you have verified the documentation manually.`)) {
                            const updateData: any = {};
                            const now = new Date().toISOString();
                            if (item.field === 'dateOfBirth') {
                              const dob = prompt('Enter date of birth (YYYY-MM-DD):', '1990-01-01');
                              if (!dob) return;
                              updateData.dateOfBirth = dob;
                            } else if (item.field === 'workAuthStatus') {
                              updateData.workAuthStatus = 'verified';
                            } else if (item.field === 'bankAccountId') {
                              alert('Bank account cannot be overridden. The employee must connect their bank account.');
                              return;
                            } else {
                              updateData[item.field] = now;
                            }
                            staffApi.update(selectedEmployee.id, updateData)
                              .then(() => {
                                queryClient.invalidateQueries({ queryKey: ['staff'] });
                                const updatedEmployee = { ...selectedEmployee };
                                if (item.field === 'workAuthStatus') {
                                  updatedEmployee.workAuthStatus = 'verified';
                                } else if (item.field === 'dateOfBirth') {
                                  updatedEmployee.dateOfBirth = updateData.dateOfBirth;
                                } else {
                                  (updatedEmployee as any)[item.field] = now;
                                }
                                setSelectedEmployee(updatedEmployee);
                              })
                              .catch(() => alert('Failed to update'));
                          }
                        }
                      }}
                      className={clsx(
                        'flex items-center gap-3 p-3 rounded-lg border w-full text-left transition-colors',
                        item.completed
                          ? 'bg-green-50 border-green-200 hover:bg-green-100'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      )}
                    >
                      <div className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        item.completed ? 'bg-green-100' : 'bg-slate-200'
                      )}>
                        {item.completed ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <item.icon className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <span className={clsx(
                        'flex-1',
                        item.completed ? 'text-green-700' : 'text-slate-600'
                      )}>
                        {item.label}
                      </span>
                      {item.completed ? (
                        <span className="text-xs text-green-600 font-medium">Complete</span>
                      ) : (
                        <span className="text-xs text-amber-600 font-medium">Click to Override</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact & Personal Info */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-slate-600" />
                    Contact Information
                  </h3>
                  {!editingContact ? (
                    <button
                      onClick={startEditingContact}
                      className="btn btn-secondary btn-sm flex items-center gap-1"
                    >
                      <Edit2 className="w-3 h-3" />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingContact(false)}
                        className="btn btn-secondary btn-sm"
                        disabled={savingContact}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveContactInfo}
                        className="btn btn-primary btn-sm"
                        disabled={savingContact}
                      >
                        {savingContact ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {editingContact ? (
                  <div className="space-y-4 bg-slate-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Email</label>
                        <input
                          type="email"
                          value={editContactData.email}
                          onChange={(e) => setEditContactData({ ...editContactData, email: e.target.value })}
                          className="input w-full"
                          placeholder="employee@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={editContactData.phone}
                          onChange={(e) => setEditContactData({ ...editContactData, phone: e.target.value })}
                          className="input w-full"
                          placeholder="555-0100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Street Address</label>
                      <input
                        type="text"
                        value={editContactData.addressStreet1}
                        onChange={(e) => setEditContactData({ ...editContactData, addressStreet1: e.target.value })}
                        className="input w-full"
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">City</label>
                        <input
                          type="text"
                          value={editContactData.addressCity}
                          onChange={(e) => setEditContactData({ ...editContactData, addressCity: e.target.value })}
                          className="input w-full"
                          placeholder="New York"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">State</label>
                        <input
                          type="text"
                          value={editContactData.addressState}
                          onChange={(e) => setEditContactData({ ...editContactData, addressState: e.target.value })}
                          className="input w-full"
                          placeholder="NY"
                          maxLength={2}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">ZIP</label>
                        <input
                          type="text"
                          value={editContactData.addressPostalCode}
                          onChange={(e) => setEditContactData({ ...editContactData, addressPostalCode: e.target.value })}
                          className="input w-full"
                          placeholder="10001"
                        />
                      </div>
                    </div>
                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Emergency Contact</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Name</label>
                          <input
                            type="text"
                            value={editContactData.emergencyContactName}
                            onChange={(e) => setEditContactData({ ...editContactData, emergencyContactName: e.target.value })}
                            className="input w-full"
                            placeholder="Jane Doe"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={editContactData.emergencyContactPhone}
                            onChange={(e) => setEditContactData({ ...editContactData, emergencyContactPhone: e.target.value })}
                            className="input w-full"
                            placeholder="555-0101"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Email</p>
                      <p className="text-slate-900">{selectedEmployee.email || 'Not provided'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Phone</p>
                      <p className="text-slate-900">{selectedEmployee.phone || 'Not provided'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 col-span-2">
                      <p className="text-xs text-slate-500 mb-1">Address</p>
                      <p className="text-slate-900">
                        {selectedEmployee.addressStreet1
                          ? `${selectedEmployee.addressStreet1}, ${selectedEmployee.addressCity || ''} ${selectedEmployee.addressState || ''} ${selectedEmployee.addressPostalCode || ''}`
                          : 'Not provided'}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Emergency Contact</p>
                      <p className="text-slate-900">
                        {selectedEmployee.emergencyContactName || 'Not provided'}
                        {selectedEmployee.emergencyContactPhone && ` • ${selectedEmployee.emergencyContactPhone}`}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Date of Birth</p>
                      <p className="text-slate-900">
                        {selectedEmployee.dateOfBirth
                          ? new Date(selectedEmployee.dateOfBirth).toLocaleDateString()
                          : 'Not provided'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Employment Details */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  Employment Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Hourly Rate</p>
                    <p className="text-slate-900 font-semibold">
                      {selectedEmployee.hourlyRateCents
                        ? `$${(selectedEmployee.hourlyRateCents / 100).toFixed(2)}/hr`
                        : 'Not set'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Bank Account</p>
                    <p className={clsx(
                      'font-semibold',
                      selectedEmployee.bankAccountId ? 'text-green-600' : 'text-amber-600'
                    )}>
                      {selectedEmployee.bankAccountId ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Work Auth Status</p>
                    <p className={clsx(
                      'font-semibold capitalize',
                      selectedEmployee.workAuthStatus === 'verified' ? 'text-green-600' :
                      selectedEmployee.workAuthStatus === 'expired' ? 'text-red-600' : 'text-amber-600'
                    )}>
                      {selectedEmployee.workAuthStatus?.replace(/_/g, ' ') || 'Pending'}
                    </p>
                  </div>
                  {selectedEmployee.workAuthExpiresAt && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Work Auth Expires</p>
                      <p className="text-slate-900">
                        {new Date(selectedEmployee.workAuthExpiresAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Availability Section */}
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-slate-600" />
                  Weekly Availability
                </h3>
                {loadingAvailability ? (
                  <p className="text-sm text-slate-400">Loading availability…</p>
                ) : employeeAvailability.length === 0 ? (
                  <p className="text-sm text-slate-400">
                    No availability set. {selectedEmployee.firstName} can set it in the staff app — until then they're treated as always available.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
                      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dow];
                      const windows = employeeAvailability
                        .filter((w) => w.dayOfWeek === dow)
                        .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
                      return (
                        <div key={dow} className="flex items-start gap-3 text-sm">
                          <span className="w-24 flex-shrink-0 text-slate-500">{dayName}</span>
                          {windows.length === 0 ? (
                            <span className="text-slate-300">Unavailable</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {windows.map((w) => (
                                <span
                                  key={w.id}
                                  className={clsx(
                                    'px-2 py-0.5 rounded-full text-xs',
                                    w.status === 'approved'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : w.status === 'pending'
                                      ? 'bg-amber-50 text-amber-700'
                                      : 'bg-slate-100 text-slate-500'
                                  )}
                                >
                                  {w.startTime}–{w.endTime}
                                  {w.status !== 'approved' ? ` · ${w.status}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    Compliance Documents
                  </h3>
                  <label className="btn btn-secondary btn-sm flex items-center gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Upload
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      disabled={uploadingDocument}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const docType = prompt(
                            'Select document type:\n\n1. Photo ID\n2. Work Authorization\n3. W-4 Tax Form\n4. Food Handler Certificate\n5. Alcohol Server Certificate\n6. Other\n\nEnter number:',
                            '1'
                          );
                          const typeMap: Record<string, string> = {
                            '1': 'id_document',
                            '2': 'work_authorization',
                            '3': 'w4_form',
                            '4': 'food_handler_cert',
                            '5': 'alcohol_server_cert',
                            '6': 'other',
                          };
                          const documentType = typeMap[docType || '6'] || 'other';
                          handleDocumentUpload(file, documentType);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>

                {loadingDocuments ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : employeeDocuments.length === 0 ? (
                  <div className="bg-slate-50 rounded-lg p-4 text-center text-sm text-slate-500">
                    No documents uploaded yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {employeeDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className={clsx(
                          'flex items-center justify-between rounded-lg px-3 py-2 border',
                          doc.isVerified
                            ? 'bg-green-50 border-green-200'
                            : 'bg-slate-50 border-slate-200'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {doc.mimeType?.startsWith('image/') ? (
                            <Image className="w-5 h-5 text-slate-400" />
                          ) : (
                            <File className="w-5 h-5 text-slate-400" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-700">
                              {getDocumentTypeLabel(doc.documentType)}
                            </p>
                            <p className="text-xs text-slate-500 truncate max-w-[180px]">
                              {doc.fileName}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.isVerified ? (
                            <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-100 rounded">
                              Verified
                            </span>
                          ) : (
                            <button
                              onClick={() => openVerifyModal(doc)}
                              className="text-xs text-primary-600 hover:text-primary-700 px-2 py-1 hover:bg-primary-50 rounded"
                              title="Review and verify document"
                            >
                              Review
                            </button>
                          )}
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                            title="View document"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={doc.fileUrl}
                            download={doc.fileName}
                            className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1.5 hover:bg-red-50 rounded text-slate-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {uploadingDocument && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </div>
                )}
              </div>

              {/* Training Status Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-slate-600" />
                    Training Status
                  </h3>
                  <button
                    onClick={async () => {
                      if (selectedEmployee && confirm(`Assign all required trainings for ${selectedEmployee.firstName}'s role (${selectedEmployee.primaryRole})?`)) {
                        try {
                          const response = await trainingsApi.assignForRole({
                            employeeId: selectedEmployee.id,
                            role: selectedEmployee.primaryRole,
                          });
                          alert(response.data?.message || 'Trainings assigned');
                          await fetchEmployeeTrainings(selectedEmployee.id);
                        } catch (error: any) {
                          alert(error.response?.data?.error || 'Failed to assign trainings');
                        }
                      }
                    }}
                    className="btn btn-secondary btn-sm flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Assign Role Trainings
                  </button>
                </div>

                {loadingTrainings ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : employeeTrainings.length === 0 ? (
                  <div className="bg-slate-50 rounded-lg p-4 text-center text-sm text-slate-500">
                    <GraduationCap className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No trainings assigned yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {employeeTrainings.map((training) => (
                      <div
                        key={training.id}
                        className={clsx(
                          'flex items-center justify-between rounded-lg px-3 py-2 border',
                          training.status === 'completed' && 'bg-green-50 border-green-200',
                          training.status === 'in_progress' && 'bg-blue-50 border-blue-200',
                          training.status === 'expired' && 'bg-red-50 border-red-200',
                          training.status === 'pending' && 'bg-slate-50 border-slate-200'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            'w-8 h-8 rounded-full flex items-center justify-center',
                            training.status === 'completed' && 'bg-green-100',
                            training.status === 'in_progress' && 'bg-blue-100',
                            training.status === 'expired' && 'bg-red-100',
                            training.status === 'pending' && 'bg-slate-200'
                          )}>
                            {training.status === 'completed' ? (
                              <Award className="w-4 h-4 text-green-600" />
                            ) : training.status === 'in_progress' ? (
                              <PlayCircle className="w-4 h-4 text-blue-600" />
                            ) : training.status === 'expired' ? (
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                            ) : (
                              <GraduationCap className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">
                              {training.trainingType?.name || 'Unknown Training'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {training.trainingType?.category && (
                                <span className="capitalize">{training.trainingType.category}</span>
                              )}
                              {training.dueDate && (
                                <span> • Due: {new Date(training.dueDate).toLocaleDateString()}</span>
                              )}
                              {training.completedAt && (
                                <span> • Completed: {new Date(training.completedAt).toLocaleDateString()}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {training.status === 'pending' && (
                            <button
                              onClick={() => handleStartTraining(training.id)}
                              className="text-xs text-blue-600 hover:text-blue-700 px-2 py-1 hover:bg-blue-50 rounded"
                            >
                              Start
                            </button>
                          )}
                          {(training.status === 'pending' || training.status === 'in_progress') && (
                            <button
                              onClick={() => handleCompleteTraining(training.id)}
                              className="text-xs text-green-600 hover:text-green-700 px-2 py-1 hover:bg-green-50 rounded"
                            >
                              Complete
                            </button>
                          )}
                          {training.status === 'expired' && (
                            <span className="text-xs text-red-600 font-medium px-2 py-1 bg-red-100 rounded">
                              Expired
                            </span>
                          )}
                          {training.status === 'completed' && (
                            <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-100 rounded">
                              ✓ Complete
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Training Progress Summary */}
                {employeeTrainings.length > 0 && (
                  <div className="mt-3 bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Training Progress</span>
                      <span className="font-medium">
                        {employeeTrainings.filter(t => t.status === 'completed').length} / {employeeTrainings.length} completed
                      </span>
                    </div>
                    <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{
                          width: `${(employeeTrainings.filter(t => t.status === 'completed').length / employeeTrainings.length) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Scheduling Restrictions */}
              {selectedEmployee.schedulingRestrictions && Object.keys(selectedEmployee.schedulingRestrictions).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Scheduling Restrictions
                  </h3>
                  <ul className="space-y-1 text-sm text-amber-700">
                    {Object.entries(selectedEmployee.schedulingRestrictions).map(([key, value]) =>
                      value && (
                        <li key={key} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          {key.replace(/_/g, ' ').replace(/no /i, 'Cannot work ')}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                {selectedEmployee.onboardingStatus !== 'ready' && (
                  <button
                    onClick={() => {
                      if (confirm(`Mark ${selectedEmployee.firstName} as ready to schedule? This overrides any pending compliance requirements.`)) {
                        staffApi.update(selectedEmployee.id, { onboardingStatus: 'ready', canBeScheduled: true } as any)
                          .then(() => {
                            queryClient.invalidateQueries({ queryKey: ['staff'] });
                            setSelectedEmployee({ ...selectedEmployee, onboardingStatus: 'ready', canBeScheduled: true });
                            alert(`${selectedEmployee.firstName} is now marked as ready to schedule.`);
                          })
                          .catch(() => alert('Failed to update status'));
                      }
                    }}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark as Ready
                  </button>
                )}
                {!selectedEmployee.bankAccountId && (
                  <button
                    onClick={() => {
                      setBankInviteModal({
                        isOpen: true,
                        employeeId: selectedEmployee.id,
                        employeeName: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
                        employeeEmail: selectedEmployee.email,
                      });
                      setSelectedEmployee(null);
                    }}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    Connect Bank
                  </button>
                )}
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="btn btn-secondary ml-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bank Invite Modal */}
      {bankInviteModal && (
        <BankInviteModal
          isOpen={bankInviteModal.isOpen}
          onClose={() => setBankInviteModal(null)}
          employeeId={bankInviteModal.employeeId}
          employeeName={bankInviteModal.employeeName}
          employeeEmail={bankInviteModal.employeeEmail}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['staff'] });
            setBankInviteModal(null);
          }}
        />
      )}

      {/* Document Verification Modal */}
      {verifyingDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Review Document</h2>
                <p className="text-sm text-slate-500">
                  {getDocumentTypeLabel(verifyingDocument.documentType)} • {verifyingDocument.fileName}
                </p>
              </div>
              <button
                onClick={() => setVerifyingDocument(null)}
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Document Preview */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Document Preview</label>
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  {verifyingDocument.mimeType?.startsWith('image/') ? (
                    <img
                      src={verifyingDocument.fileUrl}
                      alt={verifyingDocument.fileName}
                      className="w-full max-h-64 object-contain"
                    />
                  ) : (
                    <div className="p-8 text-center">
                      <File className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">{verifyingDocument.fileName}</p>
                      <a
                        href={verifyingDocument.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <Eye className="w-4 h-4" />
                        Open in new tab
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Verification Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Document Number
                    </label>
                    <input
                      type="text"
                      value={verificationData.documentNumber}
                      onChange={(e) => setVerificationData(d => ({ ...d, documentNumber: e.target.value }))}
                      placeholder="e.g., License #, Cert #"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Issuing Authority
                    </label>
                    <input
                      type="text"
                      value={verificationData.issuingAuthority}
                      onChange={(e) => setVerificationData(d => ({ ...d, issuingAuthority: e.target.value }))}
                      placeholder="e.g., State of NY, ServSafe"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    value={verificationData.expirationDate}
                    onChange={(e) => setVerificationData(d => ({ ...d, expirationDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave blank if document doesn't expire</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Verification Notes
                  </label>
                  <textarea
                    value={verificationData.verificationNotes}
                    onChange={(e) => setVerificationData(d => ({ ...d, verificationNotes: e.target.value }))}
                    placeholder="Any notes about the verification (optional)"
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setVerifyingDocument(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg"
              >
                Cancel
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (confirm('Reject this document? The employee will need to upload a new one.')) {
                      handleDeleteDocument(verifyingDocument.id);
                      setVerifyingDocument(null);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Reject Document
                </button>
                <button
                  onClick={handleVerifyDocument}
                  disabled={submittingVerification}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {submittingVerification ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Approve & Verify
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
